// Install: npm install @mui/material @mui/icons-material @mui/x-data-grid
import React, { useEffect, useMemo, useState, useRef, useCallback } from "react";
import {
  Box,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Grid,
  Paper,
  Typography,
  Button,
  Autocomplete,
  TextField,
  Checkbox,
  Backdrop,
  Snackbar,
  Alert,
  CircularProgress,
  Tooltip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  History as HistoryIcon,
  Close as CloseIcon,
  PersonSearch as PersonSearchIcon,
  HowToReg as HowToRegIcon,
} from "@mui/icons-material";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import { styled } from "@mui/material/styles";
import { DataGrid } from "@mui/x-data-grid";
import { AgGridReact } from "ag-grid-react";
import { AllCommunityModule, themeQuartz } from "ag-grid-community";
import ExportIcon from "../components/ExportIcon";
import rotationListReportConfig from "../config/rotationListReportConfig";
import { DEFAULT_PAGE_SIZE, DEFAULT_PAGE_SIZE_OPTIONS, DEFAULT_PAGE_SIZE_OPTIONS_MUI } from "../config/maintenanceConfig";
import { reportDateSuffix } from "../utils/reportDates";
import { toDisplayName } from "../utils/columnUtils";
import dayjs from "dayjs";
import "./../css/agGridCustom.css";

import api from "../api";

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

/* ======= Styling constants and StyledDataGrid ======= */
const primaryColor = "#005EB8";
const darkGrey = "#D3D3D3";

const StyledDataGrid = styled(DataGrid)(({ theme }) => ({
  "& .MuiDataGrid-columnHeader": {
    "& .MuiDataGrid-columnHeaderTitleContainer": {
      "& .MuiDataGrid-columnHeaderTitle": {
        fontWeight: "bold !important",
        whiteSpace: "normal",
        wordBreak: "break-word",
      },
    },
    backgroundColor: primaryColor,
    color: "#FFFFFF !important",
    fontSize: "0.95rem !important",
    textTransform: "none",
    borderBottom: `2px solid ${primaryColor}`,
  },
  "& .MuiDataGrid-root": {
    height: "600px",
  },
  "& .MuiDataGrid-cell": {
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    borderBottom: `1px solid ${darkGrey}`,
  },
  "& .MuiDataGrid-columnHeaders": {
    whiteSpace: "normal",
  },
  "& .editable-cell": {
    backgroundColor: "#FFFDE7 !important",
    "&:hover": {
      backgroundColor: "#FFF9C4 !important",
    },
  },
  "& .MuiDataGrid-row.row-editing .MuiDataGrid-cell--editable": {
    backgroundColor: "#FFF59D !important",
    border: "1px solid #005EB8",
  },
  "& .MuiDataGrid-row.row-editing .MuiDataGrid-cell:not(.MuiDataGrid-cell--editable)": {
    backgroundColor: "#E0E0E0 !important",
  },
}));


/* ======= Templates from config ======= */
const { reportKey: REPORT_KEY } = rotationListReportConfig;
const SHOW_ACTIONS_COLUMN = true;
// Static fallbacks — overridden at runtime by DB config
const _staticTableTemplate = rotationListReportConfig.tableTemplate;
const _staticFilterTemplate = rotationListReportConfig.filterTemplate;
const configEditableColumns = rotationListReportConfig.editableColumns;
const auditColumns = rotationListReportConfig.auditColumns;

/* ======= Helpers ======= */
// Convert apiKey (snake_case or camelCase) to human readable Title Case
const toHuman = (apiKey) =>
  String(apiKey)
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2") // camelCase -> spaces
    .replace(/_/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());

// Map order template entries (human names) to API keys using generated human names
const mapOrderTemplateToApiKeys = (orderTemplate, allApiKeys) => {
  const humanMap = {};
  allApiKeys.forEach((k) => {
    humanMap[toHuman(k).toLowerCase()] = k;
  });

  const mapped = [];
  orderTemplate.forEach((entry) => {
    const entryLower = String(entry).toLowerCase().trim();
    // 1) exact match to generated human name
    if (humanMap[entryLower]) {
      mapped.push(humanMap[entryLower]);
      return;
    }
    // 2) entry might already be an api key
    if (allApiKeys.includes(entry)) {
      mapped.push(entry);
      return;
    }
    // 3) fuzzy: remove spaces and compare
    const compact = entryLower.replace(/\s+/g, "");
    const found = allApiKeys.find(
      (k) => toHuman(k).toLowerCase().replace(/\s+/g, "") === compact
    );
    if (found) {
      mapped.push(found);
      return;
    }
    // 4) fallback: keep entry as-is (will be ignored if not present in data)
    mapped.push(entry);
  });

  // preserve order and uniqueness
  return Array.from(new Set(mapped));
};

const toRow = (item, idField = "rotation_list_main_id") => ({
  id: item[idField] ?? Math.random().toString(36).slice(2, 9),
  ...item,
  planned_date_of_rotation: item["planned_date_of_rotation"] ? item["planned_date_of_rotation"].split("T")[0] : "",
  report_extraction_date: item["report_extraction_date"] ? item["report_extraction_date"].split("T")[0] : "",
  import_timestamp: item["import_timestamp"] ? (new Date(item["import_timestamp"]).toLocaleString()) : "",
  so_mapping_date: item["so_mapping_date"] ? (new Date(item["so_mapping_date"]).toLocaleString()) : "",
});

/* ======= Main component ======= */
export default function RotationListReport() {
  const baseURI = process.env.REACT_APP_API_BASE_URL ?? "";
  const [loading, setLoading] = useState(true);
  const gridRef = useRef(null);
  const [allRows, setAllRows] = useState([]);
  const [rows, setRows] = useState([]);
  const [availableDates, setAvailableDates] = useState([]);
  const [selectedDate, setSelectedDate] = useState("");
  const [filters, setFilters] = useState({ region: ["India"] });
  const [distinctValues, setDistinctValues] = useState({});
  const [columnOrderApiKeys, setColumnOrderApiKeys] = useState([]);
  const [allApiKeys, setAllApiKeys] = useState([]);
  const [humanReadableMap, setHumanReadableMap] = useState({});
  const [columnVisibilityModel, setColumnVisibilityModel] = useState(() => {
    const saved = localStorage.getItem(REPORT_KEY);
    return saved ? JSON.parse(saved) : {};
  });

  const [dropdownOptions, setDropdownOptions] = useState({});

  const [validationOpen, setValidationOpen] = useState(false);
  const [validationMsg, setValidationMsg] = useState("");
  const [editFieldErrors, setEditFieldErrors] = useState({});
  const lastEditRowId = useRef(null);

  // Edit Modal State
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingRow, setEditingRow] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [editSaving, setEditSaving] = useState(false);

  const [auditOpen, setAuditOpen] = useState(false);
  const [selectedUnique, setSelectedUnique] = useState(null);
  const [auditData, setAuditData] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);

  // Live config from DB (falls back to static config)
  const [tableTemplate, setTableTemplate] = useState(_staticTableTemplate);
  const [filterTemplate, setFilterTemplate] = useState(_staticFilterTemplate);
  const [configReady, setConfigReady] = useState(false);
  const ordercolumnsemplate = tableTemplate.map((c) => c.field);
  const filtertemplate = filterTemplate.map((c) => c.field);
  const configHeaderMap = Object.fromEntries(tableTemplate.map((c) => [c.field, c.headerName]));


  const editableConfig = configEditableColumns;
  const editableMeta = Array.isArray(editableConfig)
    ? editableConfig.map((item) => {
      if (typeof item === 'string') {
        return { apiField: item, field: item, headerName: item };
      }
      // item is object
      const apiField = item.apiField || item.field;
      const headerName = item.headerName || item.field || apiField;
      const fieldDisplay = item.field && item.apiField ? item.field : headerName;
      return { apiField, field: fieldDisplay, headerName };
    })
    : [];

  // editableColumns used throughout code should be api field names (data keys)
  const editableColumns = editableMeta.map((e) => e.apiField);

  const fieldToDropdownTypeMap = {
    "Talent Plan": "TALENT_PLAN",
    "Replacement Plan": "REPLACEMENT_PLAN",
  };

  useEffect(() => {
    let isMounted = true;
    const fetchAllDropdownOptions = async () => {
      const options = {};
      for (const field in fieldToDropdownTypeMap) {
        const dropdownType = fieldToDropdownTypeMap[field];
        try {
          const response = await api.get(`/demand/dropdown`, {
            params: { fieldName: dropdownType },
          });
          options[field] = response.data.map((item) => ({
            value: item.key_value,
            label: item.description,
          }));
        } catch (error) {
          console.error(`Error fetching dropdown options for ${field}:`, error);
          options[field] = []; // Ensure there's always a value
        }
      }

      if (isMounted) {
        setDropdownOptions(options);
      }
    };

    fetchAllDropdownOptions();

    return () => {
      isMounted = false;
    };
  }, []);

  // Fetch live config from API on mount
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${baseURI}/api/report-config/${REPORT_KEY}`);
        if (res.ok) {
          const config = await res.json();
          if (Array.isArray(config.table_template) && config.table_template.length > 0) {
            setTableTemplate(config.table_template);
            const hiddenMap = {};
            config.table_template.forEach((c) => { if (c.hidden) hiddenMap[c.field] = false; });
            if (Object.keys(hiddenMap).length > 0) {
              setColumnVisibilityModel((prev) => ({ ...hiddenMap, ...prev }));
            }
          }
          if (Array.isArray(config.filter_template) && config.filter_template.length > 0)
            setFilterTemplate(config.filter_template);
        }
      } catch (_) { /* use static fallback */ } finally {
        setConfigReady(true);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* Initial call to get availableReportDates (and possibly data) */
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const res = await fetch(`${baseURI}/api/pool/supplyData`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ source: "rotation_list" }),
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const resp = await res.json();
        const avail = resp?.availableReportDates ?? [];
        setAvailableDates(avail);
        const defaultDate = avail[0] ?? null;
        if (defaultDate) {
          setSelectedDate(defaultDate);
          await fetchData(defaultDate);
        } else {
          const rowsData = resp?.data ?? resp ?? [];
          const mapped = (rowsData || []).map((r) => toRow(r)).filter((row) => {
            const keys = Object.keys(row).filter(k => k !== 'id');
            return keys.some(k => row[k] !== null && row[k] !== undefined && row[k] !== '');
          });
          setAllRows(mapped);
          setRows(mapped);
        }
      } catch (err) {
        console.error("initial fetch error", err);
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* Fetch data for selected date */
  const fetchData = async (date) => {
    setLoading(true);
    try {
      const payload = { source: "rotation_list", reportExtractionDate: date };
      const res = await fetch(`${baseURI}/api/pool/supplyData`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const resp = await res.json();
      const rowsData = resp?.data ?? [];
      console.log('============= rowsData first 50 = ', rowsData.slice(0, 50))
      const mapped = (rowsData || []).map((r) => toRow(r)).filter((row) => {
        const keys = Object.keys(row).filter(k => k !== 'id');
        return keys.some(k => row[k] !== null && row[k] !== undefined && row[k] !== '');
      });
      setAllRows(mapped);
      setRows(mapped);
    } catch (err) {
      console.error("fetchData error", err);
      setAllRows([]);
      setRows([]);
      setDistinctValues({});
    } finally {
      setLoading(false);
    }
  };

  /* Build human-readable map, map order template to API keys, and set column order */
  const initMappingsAndColumns = (dataRows, orderTpl = ordercolumnsemplate, headerMap = configHeaderMap) => {
    const sample = dataRows[0] ?? {};
    const apiKeys = Object.keys(sample).filter((k) => k !== "id");

    // Build humanReadableMap: use config headerName if available, else auto-generate
    const hr = {};
    apiKeys.forEach((k) => {
      hr[k] = headerMap[k] ?? toHuman(k);
    });
    setHumanReadableMap(hr);

    // Map order template to api keys using generated human names
    const mappedOrder = mapOrderTemplateToApiKeys(orderTpl, apiKeys);

    // Keep only those mapped keys that actually exist in the data
    const finalOrder = mappedOrder.filter((k) => apiKeys.includes(k));

    // Get remaining columns not in the template
    const remainingKeys = apiKeys.filter((k) => !finalOrder.includes(k));

    // Combine: ordered columns first, then remaining
    const allOrderedKeys = [...finalOrder, ...remainingKeys];
    setColumnOrderApiKeys(allOrderedKeys);
    setAllApiKeys(apiKeys);
  };

  /* Compute distinct values for filtertemplate (client-side) */
  const computeDistinctValues = (dataRows, fltTpl = filtertemplate) => {
    const dv = {};
    fltTpl.forEach((apiKey) => {
      const setVals = new Set();
      dataRows.forEach((r) => {
        const val = r[apiKey];
        if (val !== null && val !== undefined) setVals.add(String(val));
      });
      dv[apiKey] = Array.from(setVals).sort();
    });
    setDistinctValues(dv);
  };

  // Determine if a given field is required in the edit modal based on current form state
  const isFieldRequired = (field) => {
    const alwaysRequired = new Set([
      "Associate ID",
      "Associate Name",
    ]);
    if (alwaysRequired.has(field)) return true;
    return false;
  };

  // Determine if a given field is read only in the edit modal based on current form state
  const isFieldReadOnly = (field) => {
    const fieldsReadOnly = new Set([
      "Associate ID",
      "Associate Name",
      "Replacement Asso. Name",
      "Replacement Asso. Grade",
    ]);
    if (fieldsReadOnly.has(field)) return true;
    return false;
  };

  const handleEditClick = useCallback((id) => () => {
    const row = rows.find((r) => r.id === id);
    console.log('============== handleEditClick row, id = ', row, id)
    if (row) {
      handleEmployeeIdLookup(row.replacement_genc_asso_id);
      setEditingRow(row);
      // Initialize form data with current row values for editable columns
      const formData = {};
      editableColumns.forEach((col) => {
        formData[col] = row[col] || "";
      });
      formData.id = id;
      console.log('=================== formData = ', formData)
      setEditFormData(formData);
      setEditModalOpen(true);
    }
  }, [rows, editableColumns]
  );

  // Handle form field change in edit modal
  const handleEditFormChange = (field, value) => {
    console.log('=============== handleEditFormChange field, value = ', field, value)
    setEditFormData((prev) => {
      const newData = { ...prev, [field]: value };
      return newData;
    });
  }

  useEffect(() => {
    setEditFormData((prev) => ({
      ...prev,
      plan_for_rotation: computeRotationMonth(editFormData.planned_date_of_rotation),
    }));
  }, [editFormData.planned_date_of_rotation]);

  // Handle save from edit modal
  const handleEditModalSave = async () => {

    if (!editingRow) return;

    try {
      const loggedInUserDetails = localStorage.getItem("loggedInUserDetails");
      const userDetails = JSON.parse(loggedInUserDetails);
      const userName = userDetails.employee_name || "";
      const userId = userDetails.employeeId;

      const payload = {};
      editableMeta.forEach((rowConfig) => {
        payload[rowConfig['field']] = editFormData[rowConfig['apiField']] || "";
      });
      payload["Rotation Planned Date"] = payload["Rotation Planned Date"] ? dayjs(payload["Rotation Planned Date"]).format('DD-MM-YYYY') : "";

      delete payload["Associate Name"];
      delete payload["Replacement Asso. Name"];
      delete payload["Replacement Asso. Grade"];

      payload["Last Updated By ID"] = userId;
      payload["Last Updated By Name"] = userName;
      payload["Last Updated On"] = dayjs().format('DD-MM-YYYY HH:mm:ss');

      const token = localStorage.getItem("token");
      const headers = { headers: { Authorization: `Bearer ${token}` } };
      await api.post("/rotation-v2/updateAuditHistory", payload, headers);
      await api.post("/rotation-v2/updateMain", payload, headers);

      const rowUpdater = (prevData) =>
        prevData.map((row) => {
          if (row.id === editFormData.id) {
            return { ...row, ...editFormData };
          }
          return row;
        });

      setAllRows(rowUpdater);
      setRows(rowUpdater);

      console.log(`Row updated successfully.`);
      handleEditModalClose();
    } catch (error) {
      console.error("Error saving row:", error);
    } finally {
      setEditSaving(false);
    }
  };

  // Close edit modal
  const handleEditModalClose = () => {
    setEditModalOpen(false);
    setEditingRow(null);
    setEditFormData({});
    setEditFieldErrors({});
  };

  const handleAuditClick = useCallback((id) => async () => {
    const row = rows.find((r) => r.id === id);
    console.log('============== handleAuditClick row, id = ', row, id)
    if (!row) return;
    setAuditOpen(true);
    setAuditLoading(true);
    try {
      const token = localStorage.getItem("token");
      const headers = { headers: { Authorization: `Bearer ${token} ` } };
      const response = await api.get("/rotation-v2/auditHistory", {
        params: { associate_id: row.associate_id },
        headers
      });
      setAuditData(response.data);
    } catch (error) {
      console.error("Error fetching audit history:", error);
    } finally {
      setAuditLoading(false);
    }
  },
    // [data]
  );

  const handleAuditClose = () => {
    setAuditOpen(false);
    setAuditData([]);
    setSelectedUnique(null);
  };

  // AG Grid: Actions cell renderer for the main demand table
  const ActionsCellRenderer = useCallback((params) => {
    const isMaint = false; // underMaintenance;
    const row = params.row;
    const id = row?.id;
    return (
      <Box sx={{ display: "flex", gap: 0.5, alignItems: "center", height: "100%" }}>
        {/* <Tooltip title={isMaint ? MAINTENANCE_TOOLTIP : "Edit"}> */}
        <Tooltip title="Edit">
          <span>
            <IconButton
              size="small"
              disabled={isMaint}
              onClick={isMaint ? undefined : handleEditClick(id)}
            >
              <EditIcon fontSize="small" sx={isMaint ? { opacity: 0.4 } : { color: '#000' }} />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Audit History">
          <IconButton size="small" onClick={handleAuditClick(id)}>
            <HistoryIcon fontSize="small" sx={{ color: '#000' }} />
          </IconButton>
        </Tooltip>
      </Box>
    );
  }, [handleEditClick, handleAuditClick]);


  /* Re-apply column order + headers + filter values whenever data or config changes */
  useEffect(() => {
    if (!allRows.length) return;
    const newOrderTpl = tableTemplate.map((c) => c.field);
    const newHeaderMap = Object.fromEntries(tableTemplate.map((c) => [c.field, c.headerName]));
    const newFltTpl = filterTemplate.map((c) => c.field);
    initMappingsAndColumns(allRows, newOrderTpl, newHeaderMap);
    computeDistinctValues(allRows, newFltTpl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allRows, tableTemplate, filterTemplate]);

  /* Apply client-side filters - supports multi-select */
  useEffect(() => {
    if (!allRows.length) {
      setRows([]);
      return;
    }
    let filtered = [...allRows];
    Object.entries(filters).forEach(([apiKey, values]) => {
      if (!values || !Array.isArray(values) || values.length === 0) return;
      filtered = filtered.filter((r) => {
        const cell = r[apiKey];
        if (cell === null || cell === undefined) return false;
        return values.includes(String(cell));
      });
    });
    setRows(filtered);
  }, [filters, allRows]);

  /* Build columns array — include all columns (ordered first, then remaining) */
  const columnsWithActions = useMemo(() => {
    // Calculate optimal width based on header and data content
    const getColumnWidth = (key) => {
      const headerText = humanReadableMap[key] ?? toHuman(key);
      const headerWidth = headerText.length * 9 + 40; // Approx char width + padding

      // Sample data to find max content width (check first 100 rows for performance)
      let maxDataWidth = 0;
      const sampleRows = allRows.slice(0, 100);
      sampleRows.forEach((row) => {
        const cellValue = row[key];
        if (cellValue !== null && cellValue !== undefined) {
          const cellWidth = String(cellValue).length * 8 + 20;
          maxDataWidth = Math.max(maxDataWidth, cellWidth);
        }
      });

      // Return the larger of header or data width, with min/max bounds
      const calculatedWidth = Math.max(headerWidth, maxDataWidth);
      return Math.min(Math.max(calculatedWidth, 80), 300); // Min 80, Max 300
    };


    return [
      ...(SHOW_ACTIONS_COLUMN ? [{
        field: "actions",
        headerName: "Actions",
        width: 120,
        pinned: "left",
        lockPosition: true,
        sortable: false,
        filter: false,
        resizable: false,
        // cellClassName: 'sticky-column',
        // headerClassName: 'sticky-header',
        renderCell: ActionsCellRenderer,
      }] : []),
      ...columnOrderApiKeys.map((key) => ({
        field: key,
        headerName: humanReadableMap[key] ?? toHuman(key),
        width: getColumnWidth(key),
        sortable: true,
      }))
    ];
  }, [columnOrderApiKeys, humanReadableMap, allRows]);

  /* Save column visibility to localStorage */
  const handleColumnVisibilityChange = (newModel) => {
    setColumnVisibilityModel(newModel);
    localStorage.setItem(REPORT_KEY, JSON.stringify(newModel));
  };

  /* Handlers */
  const onDateChange = async (e) => {
    const date = e.target.value;
    setSelectedDate(date);
    await fetchData(date);
  };

  const handleFilterChange = (apiKey, value) => {
    setFilters((prev) => ({ ...prev, [apiKey]: value }));
  };

  /* ─── In Edit Form: Derive Rotation Month (MMM-YYYY) from a dayjs/date value — view only ─── */
  const computeRotationMonth = (planned_date_of_rotation) => {
    if (!planned_date_of_rotation) return "";
    const d = dayjs(planned_date_of_rotation);
    return d.isValid() ? d.format("MMM-YYYY") : "";
  };

  const [errorMsg, setErrorMsg] = useState("");
  // Listen for global employee lookup errors dispatched by helper edit-cells
  useEffect(() => {
    const handler = (e) => {
      try {
        const msg = e && e.detail ? e.detail : "Employee lookup failed";
        setErrorMsg(msg);
      } catch (err) {
        setErrorMsg("Employee lookup failed");
      }
    };
    window.addEventListener('employeeLookupError', handler);
    return () => window.removeEventListener('employeeLookupError', handler);
  }, []);

  // Handle Employee ID lookup in edit modal
  const handleEmployeeIdLookup = async (employeeId) => {
    if (!employeeId) return;

    try {
      const token = localStorage.getItem("token");
      const headers = { headers: { Authorization: `Bearer ${token}` } };
      const response = await api.get("/employees/getEmployeeById", {
        params: { employeeId },
        headers
      });
      const data = response?.data || {};
      // If backend returned an error/message without employee details, surface it
      if ((data.message || data.error) && !data.employee_name && !data.employee_id) {
        const msg = data.message || data.error || "Replacement Associate is not available.";
        setEditFormData((prev) => ({
          ...prev,
          replacement_associate_name: "",
          replacement_associate_grade: "",
        }));
        setErrorMsg(msg);
      } else if (data && (data.employee_name || data.employee_id || data.employeeId === 0)) {
        setEditFormData((prev) => ({
          ...prev,
          replacement_associate_name: data.employee_name || data.employeeName || prev["replacement_associate_name"] || "",
          replacement_associate_grade: data.grade || prev["replacement_associate_grade"] || "",
        }));
      } else {
        // No useful data returned — clear Employee Id + dependent fields and show generic toast
        setEditFormData((prev) => ({
          ...prev,
          replacement_associate_name: "",
          replacement_associate_grade: "",
        }));
        setErrorMsg("Replacement Associate is not available.");
      }
    } catch (error) {
      console.error("Error fetching employee name:", error);
      // Clear Employee Id + dependent fields and surface a friendly toast
      setEditFormData((prev) => ({
        ...prev,
        replacement_associate_name: "",
        replacement_associate_grade: "",
      }));
      const msg = (error && error.response && (error.response.data?.message || error.response.data?.error)) || error.message || "Unable to lookup employee. Please try again.";
      setErrorMsg(msg);
    }
  };

  return (
    <Box p={1}>
      {/* Full Screen Loader */}
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading || !configReady}
      >
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
          <CircularProgress color="inherit" size={60} />
          <Typography variant="h6" color="inherit" sx={{ textAlign: "center" }}>
            Loading Rotation Report...
          </Typography>
        </Box>
      </Backdrop>

      {/* Error Toast */}
      <Snackbar open={!!errorMsg} autoHideDuration={4000} onClose={() => setErrorMsg("")}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity="error" onClose={() => setErrorMsg("")}>
          {errorMsg}
        </Alert>
      </Snackbar>

      {/* Edit Modal */}
      <Dialog
        open={editModalOpen}
        onClose={handleEditModalClose}
        fullWidth
        maxWidth={false}
        PaperProps={{ sx: { width: '90%', height: '85%', maxWidth: '90%' } }}
      >
        <DialogTitle sx={{ backgroundColor: primaryColor, color: "#fff", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          {/* <Typography variant="h6"> */}
          <Typography>
            Edit Rotation - PDL ID: {editingRow?.["pdl_id"]} [ Account Name: {editingRow?.["account_name"]} - Project Name: {editingRow?.["proj_name"]}]
          </Typography>
          <IconButton onClick={handleEditModalClose} sx={{ color: "#fff" }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ mt: 1, px: 1, height: 'calc(100% - 140px)', overflow: 'auto', '& .MuiOutlinedInput-input': { padding: '8px 10px' }, '& .MuiSelect-select': { padding: '8px 10px' } }}>
          <Grid container spacing={1} sx={{ mt: 0.5 }}>
            {editableMeta.map(({ field, apiField, headerName }) => (
              <Grid item xs={12} sm={6} md={3} key={apiField} sx={{ px: 1, py: 1 }}>
                {(() => {
                  const required = isFieldRequired(field);
                  const isReadOnly = isFieldReadOnly(field);
                  const valueEmpty = !editFormData[apiField] || String(editFormData[apiField]).trim() === "";
                  const showError = required && valueEmpty;

                  if (fieldToDropdownTypeMap[field]) {
                    // border color: blue when required (per UX) — use primaryColor
                    const selectBorderColor = required ? primaryColor : undefined;
                    return (
                      <FormControl fullWidth size="small" required={required} error={false} sx={selectBorderColor ? {
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': { borderColor: selectBorderColor },
                          '&:hover fieldset': { borderColor: selectBorderColor },
                          '&.Mui-focused fieldset': { borderColor: selectBorderColor },
                        }
                      } : {}}>
                        <InputLabel>{(headerName || field) + (required ? ' *' : '')}</InputLabel>
                        <Select
                          value={editFormData[apiField] || ""}
                          label={(headerName || field) + (required ? ' *' : '')}
                          onChange={(e) => handleEditFormChange(apiField, e.target.value)}
                          MenuProps={{ PaperProps: { sx: { maxHeight: '200px', width: '350px', '& .MuiMenuItem-root': { whiteSpace: 'normal', height: 'auto', minHeight: '32px' } } } }}
                        >
                          {((dropdownOptions[field] || [])).map((option) => (
                            <MenuItem key={option.value} value={option.value} sx={{ '&&': { whiteSpace: 'normal', height: 'auto', minHeight: '32px', py: 0.75, px: 1, lineHeight: '1.4' } }}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    );
                  } else if (field === "Rotation Planned Date") {
                    const dateVal = editFormData[apiField] ? editFormData[apiField] : "";
                    return (
                      <TextField
                        fullWidth
                        size="small"
                        label={headerName || field}
                        type="date"
                        value={dateVal}
                        onChange={(e) => handleEditFormChange(apiField, e.target.value)}
                        InputLabelProps={{ shrink: true }}
                      />
                    );

                  } else if (field === "Rotation Month") {
                    return (
                      <TextField
                        fullWidth
                        size="small"
                        label="Rotation Month"
                        value={computeRotationMonth(editFormData.planned_date_of_rotation)}
                        InputProps={{ readOnly: true }}
                        helperText="Auto-derived from Rotation Planned Date"
                      />
                    );
                  } else if (field === "Replacement GenC AssoId") {
                    const empIdError = !!editFieldErrors[field];
                    return (
                      <TextField
                        fullWidth
                        size="small"
                        label={headerName || apiField}
                        error={empIdError}
                        value={editFormData[apiField] || ""}
                        onBlur={(e) => handleEmployeeIdLookup(e.target.value)}
                        onChange={(e) => handleEditFormChange(apiField, e.target.value)}
                        sx={empIdError ? {
                          '& .MuiOutlinedInput-root': {
                            '& fieldset': { borderColor: '#d32f2f', borderWidth: 2 },
                            '&:hover fieldset': { borderColor: '#d32f2f', borderWidth: 2 },
                            '&.Mui-focused fieldset': { borderColor: '#d32f2f', borderWidth: 2 },
                          }
                        } : {}}
                      />
                    );
                  } else {
                    return (
                      <TextField
                        fullWidth
                        size="small"
                        label={headerName || apiField}
                        value={editFormData[apiField] || ""}
                        InputProps={{
                          ...(isReadOnly && { readOnly: isReadOnly }),
                        }}
                        onChange={(e) => handleEditFormChange(apiField, e.target.value)}
                      />
                    );
                  }

                })()}
              </Grid>
            ))}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: "1px solid #e0e0e0" }}>
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center' }}>
            {/* Release Associate button hidden per requirements */}
          </Box>
          <Button onClick={handleEditModalClose} variant="outlined" color="inherit">
            Cancel
          </Button>
          <Button
            onClick={() => handleEditModalSave()}
            variant="contained"
            color="primary"
            disabled={editSaving}
            startIcon={editSaving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
          >
            {editSaving ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>


      {/* Audit History Dialog */}
      <Dialog
        open={auditOpen}
        onClose={handleAuditClose}
        fullWidth

        maxWidth="lx" // increased popup width
      >
        <DialogTitle>Audit History</DialogTitle>
        <DialogContent>
          <Typography variant="subtitle1">SO ID: {selectedUnique}</Typography>
          {auditLoading ? (
            <Typography>Loading...</Typography>
          ) : (
            <div className="ag-custom" style={{
              "--primary-color": primaryColor,
              "--dark-grey": darkGrey,
              height: 400,
              width: "100%",
              marginTop: 16
            }}>
              <AgGridReact
                theme={themeQuartz}
                modules={[AllCommunityModule]}
                rowData={auditData}
                columnDefs={auditColumns.map((col) => ({
                  field: col.field,
                  headerName: col.headerName || toDisplayName(col.field),
                  width: col.width,
                  minWidth: col.width || 120,
                  sortable: true,
                  filter: true,
                  resizable: true,
                }))}
                rowHeight={42}
                headerHeight={75}
                pagination={true}
                paginationPageSize={DEFAULT_PAGE_SIZE}
                paginationPageSizeSelector={DEFAULT_PAGE_SIZE_OPTIONS}
                getRowId={(params) => String(params.data.auditid)}
                defaultColDef={{
                  sortable: true,
                  filter: true,
                  resizable: true,
                  minWidth: 120,
                  tooltipValueGetter: (params) => params.value != null ? String(params.value) : '',
                  comparator: (valueA, valueB) => {
                    const numA = Number(valueA);
                    const numB = Number(valueB);
                    if (!isNaN(numA) && !isNaN(numB)) return numA - numB;
                    return String(valueA || '').localeCompare(String(valueB || ''));
                  },
                }}
                enableCellTextSelection={true}
                ensureDomOrder={true}
              />
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleAuditClose} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={validationOpen} onClose={() => setValidationOpen(false)}>
        <DialogTitle>Validation Error</DialogTitle>
        <DialogContent>
          <Typography>{validationMsg}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setValidationOpen(false)} color="primary">
            OK
          </Button>
        </DialogActions>
      </Dialog>

      {/* Top: Filters and Report Date */}
      <Paper sx={{ p: 1, mb: 1 }}>
        <Grid container spacing={1} alignItems="center">
          <Grid item xs={12} md={7}>
            <Typography
              variant="h5"
              sx={{
                fontWeight: "bold",
                color: primaryColor,
                marginBottom: 2,
                textAlign: "left",
              }}
            >
              Supply - Rotation List
            </Typography>
          </Grid>

          <Grid item xs={12} md={2}>
            <FormControl fullWidth>
              <InputLabel id="report-date-label">Report Date</InputLabel>
              <Select
                labelId="report-date-label"
                value={selectedDate || ""}
                label="Report Date"
                onChange={onDateChange}
                size="small"
                MenuProps={{ PaperProps: { sx: { maxHeight: '200px', width: '350px', '& .MuiMenuItem-root': { whiteSpace: 'normal', height: 'auto', minHeight: '32px' } } } }}
              >
                {availableDates.map((d) => (
                  <MenuItem key={d} value={d} sx={{ '&&': { whiteSpace: 'normal', height: 'auto', minHeight: '32px', py: 0.75, px: 1, lineHeight: '1.4' } }}>
                    {d}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={3} sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 1 }}>
            <ExportIcon
              exportRows={rows}
              exportColumns={columnsWithActions}
              exportTableRef={gridRef}
              reportName={`Rotation_${reportDateSuffix()}`}
            />
            <Button
              variant="outlined"
              size="small"
              onClick={() => setFilters({})}
              sx={{ height: 36 }}
            >
              Clear Filters
            </Button>
          </Grid>

          {/* Filters inline to the right of date on larger screens */}
          <Grid item xs={12} md={12}>
            <Grid container spacing={1}>
              {filterTemplate.map(({ field, headerName }) => {
                const options = distinctValues[field] ?? [];
                return (
                  <Grid item xs={12} sm={6} md={3} key={field}>
                    <Autocomplete
                      multiple
                      size="small"
                      options={options}
                      disableCloseOnSelect
                      getOptionLabel={(option) => option}
                      value={filters[field] ?? []}
                      onChange={(event, newValue) => handleFilterChange(field, newValue)}
                      renderOption={(props, option, { selected }) => (
                        <li {...props} style={{ whiteSpace: 'normal', height: 'auto', minHeight: '32px', padding: '8px', lineHeight: '1.4', display: 'flex', alignItems: 'flex-start' }}>
                          <Checkbox
                            icon={icon}
                            checkedIcon={checkedIcon}
                            style={{ marginRight: 8, padding: '0px', flexShrink: 0, marginTop: '2px' }}
                            checked={selected}
                          />
                          <span style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{option}</span>
                        </li>
                      )}
                      renderInput={(params) => (
                        <TextField {...params} label={headerName} placeholder={headerName} />
                      )}
                      sx={{ minWidth: 200 }}
                      ListboxProps={{ style: { maxHeight: '200px', width: '350px' } }}
                    />
                  </Grid>
                );
              })}
            </Grid>
          </Grid>
        </Grid>
      </Paper>

      {/* Bottom: DataGrid (only columns from ordercolumnsemplate are shown) */}
      <Paper sx={{ height: 450, p: 1 }}>
        <StyledDataGrid
          ref={gridRef}
          rows={rows}
          columns={columnsWithActions}
          pageSize={DEFAULT_PAGE_SIZE}
          rowsPerPageOptions={DEFAULT_PAGE_SIZE_OPTIONS_MUI}
          loading={loading}
          disableSelectionOnClick
          autoHeight={false}
          columnVisibilityModel={columnVisibilityModel}
          onColumnVisibilityModelChange={handleColumnVisibilityChange}
          // disableVirtualization
          sx={{
            border: `1px solid ${darkGrey}`,
            // '& .sticky-column': {
            //   position: 'sticky',
            //   left: 0,
            //   zIndex: 10,
            // },
            // '& .sticky-header': {
            //   position: 'sticky',
            //   left: 0,
            //   zIndex: 11,
            // },
          }}
        />
      </Paper>
    </Box>
  );
}
